import React, { useMemo, useState } from 'react';
import { MetricCard } from 'common/components';
import type { SMSStatistic } from 'types/sms.type';
import { formatCount, getSegmentColor } from '../../CallAnalytics/CallStatusSummary/callStatusSummary.utils';
import {
    ProgressBreakdown,
    type ProgressLegendItem,
} from '../../CallAnalytics/CallStatusSummary/components/ProgressBreakdown';
import {
    buildSMSStatusSummaryCards,
    getSentSMSTotal,
    SMS_STATUS_ICON_THEME,
    type SMSStatusSummaryCardVariant,
} from './smsStatusSummary.utils';

import styles from '../../CallAnalytics/CallStatusSummary/CallStatusSummary.module.scss';

export interface SMSStatusSummaryProps {
    smsStats?: SMSStatistic;
}

export const SMSStatusSummary: React.FC<SMSStatusSummaryProps> = ({ smsStats }) => {
    const cards = useMemo(() => buildSMSStatusSummaryCards(smsStats), [smsStats]);
    const [selectedCardId, setSelectedCardId] = useState<SMSStatusSummaryCardVariant>('queued');

    const sentTotal = getSentSMSTotal(smsStats);
    const selectedCard = cards.find((card) => card.id === selectedCardId) ?? cards[0];
    const selectedTheme = SMS_STATUS_ICON_THEME[selectedCard.variant];

    const deepDiveItems = useMemo<ProgressLegendItem[]>(
        () =>
            selectedCard.subStatuses.map((subStatus, index) => ({
                id: subStatus.id,
                label: subStatus.label,
                count: subStatus.count,
                percent: subStatus.percent,
                color: getSegmentColor(selectedTheme.color, index),
            })),
        [selectedCard, selectedTheme]
    );

    return (
        <section
            className={`${styles.section} flex flex-col gap-lg`}
            data-testid='sms-status-summary'
            aria-label='SMS status summary'
        >
            <div className='flex gap-x-2xl gap-y-sm items-baseline flex-wrap'>
                <h3>
                    Total SMS sent <span className='text-5xl'>{formatCount(sentTotal)}</span>
                </h3>
            </div>

            <div className={styles.grid} role='tablist' aria-label='SMS status categories'>
                {cards.map((card) => (
                    <MetricCard
                        key={card.id}
                        id={card.id}
                        label={card.label}
                        tooltip={card.tooltip}
                        theme={SMS_STATUS_ICON_THEME[card.variant]}
                        count={card.count}
                        percent={card.percent}
                        showPercent={card.id !== 'queued'}
                        isSelected={selectedCard.id === card.id}
                        onClick={(cardId) => setSelectedCardId(cardId as SMSStatusSummaryCardVariant)}
                        testId={`sms-status-card-${card.id}`}
                        tabId={`sms-status-tab-${card.id}`}
                        panelId={`sms-status-panel-${card.id}`}
                    />
                ))}
            </div>

            <ProgressBreakdown
                title={selectedCard.breakdownTitle}
                subtitle={selectedCard.breakdownSubtitle}
                items={deepDiveItems}
                testId={`sms-status-deep-dive-${selectedCard.id}`}
                panelId={`sms-status-panel-${selectedCard.id}`}
                labelledBy={`sms-status-tab-${selectedCard.id}`}
                role='tabpanel'
            />
        </section>
    );
};
